import * as React from "react";
import { App as AntdApp, ConfigProvider } from "antd";
import {
  createGlobalStyle,
  ThemeProvider as StyledComponentsThemeProvider,
} from "styled-components";
import { AppTheme, Palette, ThemeMode } from "./tokens";
import { darkTheme } from "./themes/dark";
import { lightTheme } from "./themes/light";

const themes: Record<ThemeMode, AppTheme> = {
  light: lightTheme,
  dark: darkTheme,
};

export interface ThemeContextValue {
  theme: AppTheme;
  palette: Palette;
  mode: ThemeMode;
  setMode: (mode: ThemeMode) => void;
  toggleTheme: () => void;
}

export const ThemeContext = React.createContext<ThemeContextValue>({
  theme: lightTheme,
  palette: lightTheme.palette,
  mode: lightTheme.mode,
  setMode: () => undefined,
  toggleTheme: () => undefined,
});

export interface ThemeProviderProps {
  children: React.ReactNode;
  initialMode?: ThemeMode;
}

interface ThemeCssVariablesProps {
  palette: Palette;
}

const toCssVariables = (palette: Palette) =>
  Object.entries(palette)
    .map(([key, value]) => `--${key}: ${value};`)
    .join("\n");

export const ThemeCssVariables = createGlobalStyle<ThemeCssVariablesProps>`
  :root {
    ${(props) => toCssVariables(props.palette)}
  }
`;

export const ThemeProvider = ({
  children,
  initialMode = "light",
}: ThemeProviderProps) => {
  const [mode, setMode] = React.useState<ThemeMode>(initialMode);
  const theme = themes[mode];

  const value = React.useMemo<ThemeContextValue>(
    () => ({
      theme,
      palette: theme.palette,
      mode,
      setMode,
      toggleTheme: () =>
        setMode((currentMode) => (currentMode === "light" ? "dark" : "light")),
    }),
    [mode, theme]
  );

  return (
    <ThemeContext.Provider value={value}>
      <StyledComponentsThemeProvider theme={theme.palette}>
        <ThemeCssVariables palette={theme.palette} />
        <ConfigProvider theme={theme.antd}>
          <AntdApp key="antApp">{children}</AntdApp>
        </ConfigProvider>
      </StyledComponentsThemeProvider>
    </ThemeContext.Provider>
  );
};

export const ThemeProviderConnected = ({
  children,
}: Pick<ThemeProviderProps, "children">) => {
  return <ThemeProvider>{children}</ThemeProvider>;
};
